import React from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../api/authApi";
import { request } from "../api/http";
import { useAuth } from "../auth/authStore";
import { sanitizeFullNameInput, validateFullName } from "../utils/fullName";
import { assignExternalUrl } from "../utils/safeUrl";
import brandLogo from '../assets/img/logo.svg';

const CODE_LEN = 5;
const RESEND_DEFAULT_SECONDS = 60;
const DEFAULT_SMARTCAPTCHA_SITE_KEY = "";
const CAPTCHA_SUSPENDED_SESSION_KEY = "bs:smartcaptcha:suspended";
const INVITE_ATTR_KEY = "bs:invite:psychologist";
const RUNTIME_SMARTCAPTCHA_SITE_KEY_STORAGE = "bs:smartcaptcha:sitekey";

function readInviteAttribution(searchParams) {
  let fromQueryId = null;
  const sourceFromQuery = String(searchParams?.get("source") || "").trim().toLowerCase();
  const psyRaw = String(searchParams?.get("psychologistId") || "").trim();
  if (psyRaw) {
    const token = psyRaw.split("-")[0];
    const n = Number(token);
    if (Number.isFinite(n) && n > 0) fromQueryId = n;
  }
  if (fromQueryId && sourceFromQuery === "invite") {
    return { psychologistId: fromQueryId, source: "invite" };
  }

  if (typeof window === "undefined") return null;
  try {
    const raw = JSON.parse(window.localStorage.getItem(INVITE_ATTR_KEY) || "null");
    const id = Number(raw?.psychologistId);
    const source = String(raw?.source || "").trim().toLowerCase();
    if (Number.isFinite(id) && id > 0 && source === "invite") {
      return { psychologistId: id, source: "invite" };
    }
  } catch {
    // ignore
  }
  return null;
}

function readCaptchaSuspendedSessionFlag() {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(CAPTCHA_SUSPENDED_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function persistCaptchaSuspendedSessionFlag(value) {
  if (typeof window === "undefined") return;
  try {
    if (value) window.sessionStorage.setItem(CAPTCHA_SUSPENDED_SESSION_KEY, "1");
    else window.sessionStorage.removeItem(CAPTCHA_SUSPENDED_SESSION_KEY);
  } catch {
    // noop
  }
}

function readRuntimeCaptchaSiteKey() {
  if (typeof window === "undefined") return "";
  try {
    return String(window.sessionStorage.getItem(RUNTIME_SMARTCAPTCHA_SITE_KEY_STORAGE) || "").trim();
  } catch {
    return "";
  }
}

function persistRuntimeCaptchaSiteKey(value) {
  if (typeof window === "undefined") return;
  try {
    const normalized = String(value || "").trim();
    if (normalized) {
      window.sessionStorage.setItem(RUNTIME_SMARTCAPTCHA_SITE_KEY_STORAGE, normalized);
    } else {
      window.sessionStorage.removeItem(RUNTIME_SMARTCAPTCHA_SITE_KEY_STORAGE);
    }
  } catch {
    // noop
  }
}

function isSmartCaptchaSuspendedError(errorLike) {
  const message = String(
    errorLike?.message || errorLike?.reason?.message || errorLike?.reason || errorLike || ""
  )
    .trim()
    .toLowerCase();
  return message.includes("smartcaptcha") && message.includes("suspended");
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function validatePassword(value) {
  const s = String(value || "");
  if (s.length < 8) return "Минимум 8 символов";
  if (!/[A-Za-zА-Яа-я]/.test(s) || !/\d/.test(s)) return "Добавьте буквы и цифры";
  return "";
}

function readClientLinkToken(searchParams) {
  const direct = [
    "clientAuthToken",
    "clientInviteToken",
    "magicToken",
    "directToken",
  ]
    .map((key) => String(searchParams?.get(key) || "").trim())
    .find(Boolean);
  if (direct) return direct;

  const mode = String(searchParams?.get("mode") || "").trim().toLowerCase();
  if (mode === "direct" || mode === "magic" || mode === "client-link") {
    return String(searchParams?.get("token") || "").trim();
  }

  return "";
}

function codeToDigits(raw) {
  return String(raw || "")
    .replace(/\D/g, "")
    .slice(0, CODE_LEN)
    .split("");
}

function formatDateTimeShort(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function scorePassword(value) {
  const s = String(value || "");
  if (!s) return 0;
  let score = 0;
  if (s.length >= 8) score += 1;
  if (/[A-ZА-Я]/.test(s)) score += 1;
  if (/\d/.test(s)) score += 1;
  if (/[^A-Za-zА-Яа-я0-9]/.test(s)) score += 1;
  return score;
}

function isCodeRelatedError(error) {
  const code = String(error?.code || "").toUpperCase();
  return ["CODE_INVALID", "CODE_EXPIRED", "CODE_LOCKED", "CODE_NOT_FOUND"].includes(code);
}

function mapApiError(error, fallback = "Не удалось выполнить запрос") {
  const code = String(error?.code || "").toUpperCase();
  const status = Number(error?.status || 0);
  const message = String(error?.message || error?.error || "").trim();

  if (code === "CODE_INVALID") {
    const left = Number(error?.attemptsLeft);
    if (Number.isFinite(left) && left >= 0) return `Неверный код. Осталось попыток: ${left}`;
    return "Неверный код";
  }

  if (code === "CODE_EXPIRED") return "Код истёк. Запросите новый";

  if (code === "CODE_LOCKED") {
    const retry = Number(error?.retryAfterSeconds);
    if (Number.isFinite(retry) && retry > 0) {
      const mins = Math.ceil(retry / 60);
      return `Слишком много попыток. Повторите через ${mins} мин.`;
    }
    return "Слишком много попыток. Повторите позже";
  }

  if (code === "RESEND_TOO_SOON") {
    const retry = Number(error?.retryAfterSeconds);
    if (Number.isFinite(retry) && retry > 0) return `Повторная отправка доступна через ${retry} сек.`;
    return "Код уже отправлен. Попробуйте чуть позже";
  }

  if (code === "EMAIL_ALREADY_REGISTERED") return "Email уже зарегистрирован";

  if (status === 409) return "Email уже зарегистрирован";
  if (status === 401 || code === "UNAUTHORIZED") return "Неверный email или пароль";
  if (status === 403 || code === "FORBIDDEN") return "Доступ запрещён";

  if (message.toLowerCase().includes("captcha")) return "Подтвердите, что вы не робот";

  if (message) return message;
  return fallback;
}

function mapClientLinkError(error, fallback = "Ссылка входа недействительна или уже истекла") {
  const code = String(error?.code || "").trim().toUpperCase();
  const message = String(error?.message || error?.error || "").trim();

  if (["CLIENT_LINK_INVALID", "CLIENT_LINK_EXPIRED", "TOKEN_REQUIRED"].includes(code) && message) {
    return message;
  }

  if (message.toLowerCase().includes("ссылка входа")) {
    return message;
  }

  return mapApiError(error, fallback);
}

let captchaLoaderPromise = null;

function loadCaptchaScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.smartCaptcha) return Promise.resolve(true);

  if (!captchaLoaderPromise) {
    captchaLoaderPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://smartcaptcha.yandexcloud.net/captcha.js";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  }

  return captchaLoaderPromise;
}

function SmartCaptchaBox({ siteKey, onToken, onError, resetKey, disabled }) {
  const containerIdRef = React.useRef(`smartcaptcha-${Math.random().toString(36).slice(2)}`);
  const widgetIdRef = React.useRef(null);
  const onTokenRef = React.useRef(onToken);
  const onErrorRef = React.useRef(onError);

  React.useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  React.useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      if (!siteKey) return;
      const ok = await loadCaptchaScript();
      if (!alive) return;

      if (!ok || !window.smartCaptcha) {
        onErrorRef.current?.(new Error("Не удалось загрузить капчу"));
        return;
      }

      try {
        if (widgetIdRef.current != null && window.smartCaptcha?.reset) {
          window.smartCaptcha.reset(widgetIdRef.current);
        }
      } catch {
        // noop
      }

      try {
        widgetIdRef.current = window.smartCaptcha.render(containerIdRef.current, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current?.(token || ""),
          "expired-callback": () => onTokenRef.current?.(""),
          "error-callback": () => onErrorRef.current?.(new Error("Captcha error")),
        });
      } catch (e) {
        onErrorRef.current?.(e);
      }
    })();

    return () => {
      alive = false;
      try {
        if (widgetIdRef.current != null && window.smartCaptcha?.reset) {
          window.smartCaptcha.reset(widgetIdRef.current);
        }
      } catch {
        // noop
      }
    };
  }, [resetKey, siteKey]);

  if (!siteKey) return null;

  return (
    <div className={`login-captcha ${disabled ? "is-disabled" : ""}`.trim()}>
      <div id={containerIdRef.current} />
      <div className="login-captcha-hint">Подтвердите, что вы не робот</div>
    </div>
  );
}

function OtpInputGroup({ idPrefix, value, onChange, disabled }) {
  const refs = React.useRef([]);

  const setAt = React.useCallback(
    (index, digit) => {
      const next = Array.from({ length: CODE_LEN }, (_, i) => value[i] || "");
      next[index] = digit;
      onChange(next);
    },
    [onChange, value]
  );

  return (
    <div className="otp-row" role="group" aria-label="Код подтверждения">
      {Array.from({ length: CODE_LEN }).map((_, index) => (
        <input
          key={`${idPrefix}-${index}`}
          ref={(node) => {
            refs.current[index] = node;
          }}
          className="otp-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={value[index] || ""}
          disabled={disabled}
          onChange={(event) => {
            const digit = String(event.target.value || "")
              .replace(/\D/g, "")
              .slice(0, 1);
            setAt(index, digit);
            if (digit && refs.current[index + 1]) refs.current[index + 1].focus();
          }}
          onKeyDown={(event) => {
            if (event.key === "Backspace" && !value[index] && refs.current[index - 1]) {
              refs.current[index - 1].focus();
            }
          }}
          onPaste={(event) => {
            event.preventDefault();
            const pasted = codeToDigits(event.clipboardData.getData("text"));
            const next = Array.from({ length: CODE_LEN }, (_, i) => pasted[i] || "");
            onChange(next);
            const focusIndex = Math.min(pasted.length, CODE_LEN - 1);
            refs.current[focusIndex]?.focus();
          }}
        />
      ))}
    </div>
  );
}

function LeftPanel({ page }) {
  const map = {
    login: {
      title: (
        <>
          Вся практика —<br />
          на одной <span className="accent-mint">платформе.</span>
          <br />
          <span className="accent-lav">Без лишнего.</span>
        </>
      ),
      subtitle:
        "CRM для психологов: записи клиентов, видеосессии, чаты и финансы — всё в одном месте.",
      proof: "50+ психологов",
      proofSub: "уже работают в Бюро Души",
    },
    register: {
      title: (
        <>
          Начните работу<br />
          <span className="accent-mint">бесплатно</span> —
          <br />
          <span className="accent-lav">7 дней.</span>
        </>
      ),
      subtitle: "Зарегистрируйтесь за 2 минуты. Карта не нужна. Отменить в любой момент.",
      proof: "50+ психологов",
      proofSub: "уже работают в Бюро Души",
    },
    forgot: {
      title: (
        <>
          Забыли пароль?<br />
          <span className="accent-mint">Не беда.</span>
          <br />
          <span className="accent-lav">Восстановим.</span>
        </>
      ),
      subtitle: "Укажите email — мы отправим код для восстановления в течение минуты.",
      proof: "Поддержка 24/7",
      proofSub: "поможет с любым вопросом",
    },
    reset: {
      title: (
        <>
          Почти готово —<br />
          придумайте<br />
          <span className="accent-mint">новый пароль.</span>
        </>
      ),
      subtitle: "Введите новый пароль дважды. После сохранения войдите в аккаунт.",
      proof: "Данные клиентов",
      proofSub: "под надёжной защитой",
    },
  };

  const view = map[page] || map.login;

  return (
    <div className="auth-left">
      <div className="auth-left-grid" />

      <div className="auth-left-top">
        <a className="al-logo" href="/" aria-label="Бюро Души">
          <img className="al-logo-img" src={brandLogo} alt="Бюро Души" />
        </a>

        <div className="al-headline">{view.title}</div>
        <div className="al-sub">{view.subtitle}</div>
      </div>

      <div className="al-mockup">
        <div className="al-mock-bar">
          <div className="al-mock-dot d-r" />
          <div className="al-mock-dot d-y" />
          <div className="al-mock-dot d-g" />
        </div>
        <div className="al-mock-body">
          <div className="al-mock-stats">
            <div className="al-mock-stat">
              <div className="al-mock-stat-label">Клиенты</div>
              <div className="al-mock-stat-val m">24</div>
            </div>
            <div className="al-mock-stat">
              <div className="al-mock-stat-label">Сессий</div>
              <div className="al-mock-stat-val">38</div>
            </div>
            <div className="al-mock-stat">
              <div className="al-mock-stat-label">Свободно</div>
              <div className="al-mock-stat-val l">6</div>
            </div>
          </div>

          <div className="al-mock-row">
            <div className="al-mock-av" style={{ background: "var(--mint)" }}>
              АК
            </div>
            <div className="al-mock-info">
              <div className="al-mock-name">Алина Кротова</div>
              <div className="al-mock-time">Сегодня · 14:00</div>
            </div>
            <div className="al-mock-badge mb-soon">Скоро</div>
          </div>

          <div className="al-mock-row">
            <div className="al-mock-av" style={{ background: "var(--lavender)" }}>
              ДМ
            </div>
            <div className="al-mock-info">
              <div className="al-mock-name">Дмитрий Морозов</div>
              <div className="al-mock-time">Сегодня · 16:30</div>
            </div>
            <div className="al-mock-badge mb-new">Новый</div>
          </div>

          <div className="al-mock-income">
            <div>
              <div className="al-mock-inc-label">Доход за апрель</div>
              <div className="al-mock-inc-val">76 400 ₽</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="al-mock-inc-pct">100%</div>
              <div className="al-mock-inc-pct-lbl">ваш доход</div>
            </div>
          </div>
        </div>
      </div>

      <div className="al-social-proof">
        <div className="al-avatars">
          <div className="al-av" />
          <div className="al-av" />
          <div className="al-av" />
          <div className="al-av" />
        </div>
        <div className="al-proof-text">
          <strong>{view.proof}</strong>
          <br />
          {view.proofSub}
        </div>
      </div>
    </div>
  );
}

function SocialAuthRow({ yandexAuthUrl = "" }) {
  return (
    <>
      <div className="divider">
        <div className="divider-line" />
        <span className="divider-text">или через</span>
        <div className="divider-line" />
      </div>

      <div className="social-row">
        <button
          type="button"
          className="btn-social"
          disabled={!yandexAuthUrl}
          onClick={() => {
            if (yandexAuthUrl) assignExternalUrl(yandexAuthUrl);
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M11.19 18.47h2.39V6.88h-1.85c-1.89 0-3.25 1.08-3.25 2.94 0 1.3.7 2.24 1.93 2.69L8.2 16.7h2.52l1.86-3.6h.18v5.37ZM10.9 9.9c0-.76.45-1.15 1.2-1.15h.75v2.32h-.75c-.75 0-1.2-.39-1.2-1.17Z"
              fill="#FC3F1D"
            />
          </svg>
          Яндекс ID
        </button>
      </div>
    </>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    login: authLogin,
    loginByClientLink: authLoginByClientLink,
    boot,
  } = useAuth();

  const envCaptchaSiteKey = String(
    process.env.REACT_APP_SMARTCAPTCHA_SITEKEY
      || process.env.REACT_APP_SMARTCAPTCHA_SITE_KEY
      || process.env.REACT_APP_YANDEX_SMARTCAPTCHA_SITEKEY
      || process.env.REACT_APP_CAPTCHA_SITE_KEY
      || DEFAULT_SMARTCAPTCHA_SITE_KEY
  ).trim();
  const YANDEX_AUTH_URL = String(process.env.REACT_APP_YANDEX_AUTH_URL || "").trim();
  const [runtimeCaptchaSiteKey, setRuntimeCaptchaSiteKey] = React.useState(() => readRuntimeCaptchaSiteKey());
  const [captchaConfigLoading, setCaptchaConfigLoading] = React.useState(false);
  const [captchaSuspended, setCaptchaSuspended] = React.useState(() => readCaptchaSuspendedSessionFlag());
  const isLocalRuntimeHost = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const host = String(window.location.hostname || "").trim().toLowerCase();
    return host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0";
  }, []);
  const CAPTCHA_SITE_KEY = React.useMemo(
    () => String(envCaptchaSiteKey || runtimeCaptchaSiteKey || DEFAULT_SMARTCAPTCHA_SITE_KEY).trim(),
    [envCaptchaSiteKey, runtimeCaptchaSiteKey]
  );
  const disableCaptchaByEnv = ["1", "true", "yes", "on"].includes(
    String(process.env.REACT_APP_DISABLE_SMARTCAPTCHA || "").trim().toLowerCase()
  );
  const disableCaptchaInDevLocal = process.env.NODE_ENV !== "production" && isLocalRuntimeHost;
  const captchaEnabled = Boolean(CAPTCHA_SITE_KEY) && !captchaSuspended && !disableCaptchaByEnv && !disableCaptchaInDevLocal;
  const captchaConfigMissing = !CAPTCHA_SITE_KEY && !isLocalRuntimeHost;

  React.useEffect(() => {
    if (envCaptchaSiteKey || disableCaptchaByEnv || disableCaptchaInDevLocal || isLocalRuntimeHost) return undefined;

    let cancelled = false;
    setCaptchaConfigLoading(true);

    request("/api/public/meta", { method: "GET" })
      .then((response) => {
        if (cancelled) return;
        const item = response?.item && typeof response.item === "object" ? response.item : response;
        const enabled = item?.smartCaptchaEnabled;
        const siteKey = String(item?.smartCaptchaSiteKey || "").trim();
        if (enabled === false) {
          persistRuntimeCaptchaSiteKey("");
          setRuntimeCaptchaSiteKey("");
          return;
        }
        if (siteKey) {
          persistRuntimeCaptchaSiteKey(siteKey);
          setRuntimeCaptchaSiteKey(siteKey);
        }
      })
      .catch(() => {
        if (cancelled) return;
      })
      .finally(() => {
        if (!cancelled) setCaptchaConfigLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [disableCaptchaByEnv, disableCaptchaInDevLocal, envCaptchaSiteKey, isLocalRuntimeHost]);

  const captchaHintText = React.useMemo(() => {
    if (captchaEnabled || disableCaptchaByEnv || disableCaptchaInDevLocal) {
      return "";
    }
    if (captchaConfigLoading) {
      return "Подключаем SmartCaptcha…";
    }
    if (captchaSuspended) {
      return "SmartCaptcha временно недоступна. Попробуйте повторить действие позже.";
    }
    if (captchaConfigMissing) {
      return "SmartCaptcha не активна: проверь public meta или production env.";
    }
    return "SmartCaptcha сейчас недоступна.";
  }, [captchaConfigLoading, captchaConfigMissing, captchaEnabled, captchaSuspended, disableCaptchaByEnv, disableCaptchaInDevLocal]);

  const personalData = "/documents/personal-data";
  const privacyPolicy = "/documents/privacy-policy";
  const userAgreement = "/documents/user-agreement";
  const publicOffer = "/documents/public-offer";
  const refundPolicy = "/documents/refund-policy";
  const legalDocumentsVersion = "v1_8";
  const clientLinkToken = React.useMemo(() => readClientLinkToken(searchParams), [searchParams]);

  const [page, setPage] = React.useState("login");

  const [notice, setNotice] = React.useState({ type: "", text: "" });
  const clearNotice = React.useCallback(() => setNotice({ type: "", text: "" }), []);

  const [loading, setLoading] = React.useState({
    login: false,
    directLinkLogin: false,
    directLinkSetup: false,
    register: false,
    registerVerify: false,
    registerResend: false,
    forgot: false,
    reset: false,
  });

  const [loginData, setLoginData] = React.useState({ email: "", password: "", remember: true });
  const [loginErrors, setLoginErrors] = React.useState({ email: "", password: "", captcha: "" });
  const [loginShowPassword, setLoginShowPassword] = React.useState(false);
  const [directLinkState, setDirectLinkState] = React.useState({
    stage: "idle",
    token: "",
    suggestedEmail: "",
    currentLogin: "",
    expiresAt: null,
    fullName: "",
    errorMessage: "",
  });
  const [directLinkSetup, setDirectLinkSetup] = React.useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [directLinkErrors, setDirectLinkErrors] = React.useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [directLinkShowPassword, setDirectLinkShowPassword] = React.useState(false);
  const [directLinkShowPassword2, setDirectLinkShowPassword2] = React.useState(false);

  const [registerStage, setRegisterStage] = React.useState("form");
  const [registerData, setRegisterData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    accountType: "client",
    password: "",
    confirmPassword: "",
    acceptPolicy: false,
    acceptAge: false,
    marketing: false,
  });
  const [registerErrors, setRegisterErrors] = React.useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    policy: "",
    age: "",
    captcha: "",
    code: "",
  });
  const [registerShowPassword, setRegisterShowPassword] = React.useState(false);
  const [registerShowPassword2, setRegisterShowPassword2] = React.useState(false);
  const [registerCode, setRegisterCode] = React.useState(Array.from({ length: CODE_LEN }, () => ""));
  const [registerCooldown, setRegisterCooldown] = React.useState(0);
  const [registerPendingEmail, setRegisterPendingEmail] = React.useState("");

  const [forgotStage, setForgotStage] = React.useState("form");
  const [forgotEmail, setForgotEmail] = React.useState("");
  const [forgotSentEmail, setForgotSentEmail] = React.useState("");
  const [forgotErrors, setForgotErrors] = React.useState({ email: "", captcha: "" });

  const [resetDone, setResetDone] = React.useState(false);
  const [resetData, setResetData] = React.useState({
    email: "",
    code: Array.from({ length: CODE_LEN }, () => ""),
    newPassword: "",
    confirmPassword: "",
  });
  const [resetErrors, setResetErrors] = React.useState({
    email: "",
    code: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [resetShowPassword, setResetShowPassword] = React.useState(false);
  const [resetShowPassword2, setResetShowPassword2] = React.useState(false);

  const [captchaTokens, setCaptchaTokens] = React.useState({
    login: "",
    register: "",
    registerResend: "",
    forgot: "",
  });
  const [captchaResetKeys, setCaptchaResetKeys] = React.useState({
    login: 0,
    register: 0,
    registerResend: 0,
    forgot: 0,
  });

  const onCaptchaSuspended = React.useCallback(() => {
    setCaptchaSuspended(true);
    persistCaptchaSuspendedSessionFlag(true);
    setCaptchaTokens({ login: "", register: "", registerResend: "", forgot: "" });
    setLoginErrors((prev) => ({ ...prev, captcha: "" }));
    setRegisterErrors((prev) => ({ ...prev, captcha: "" }));
    setForgotErrors((prev) => ({ ...prev, captcha: "" }));
    setNotice({
      type: "warn",
      text: "SmartCaptcha временно недоступна (suspended). Капча отключена на этой сессии.",
    });
  }, []);

  React.useEffect(() => {
    const onWindowError = (event) => {
      if (!isSmartCaptchaSuspendedError(event?.message || event?.error)) return;
      event.preventDefault?.();
      onCaptchaSuspended();
    };

    const onUnhandledRejection = (event) => {
      if (!isSmartCaptchaSuspendedError(event?.reason)) return;
      event.preventDefault?.();
      onCaptchaSuspended();
    };

    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [onCaptchaSuspended]);

  React.useEffect(() => {
    const mode = String(searchParams.get("mode") || "")
      .trim()
      .toLowerCase();

    if (["login", "register", "forgot", "reset"].includes(mode)) {
      setPage(mode);
      return;
    }

    const recovery = String(searchParams.get("recovery") || "").trim();
    const resetPassword = String(searchParams.get("resetPassword") || "").trim();
    if (recovery === "1" || resetPassword === "true") {
      setPage("forgot");
      return;
    }

    setPage("login");
  }, [searchParams]);

  React.useEffect(() => {
    if (!clientLinkToken) {
      setDirectLinkState({
        stage: "idle",
        token: "",
        suggestedEmail: "",
        currentLogin: "",
        expiresAt: null,
        fullName: "",
        errorMessage: "",
      });
      return;
    }

    let cancelled = false;
    clearNotice();
    setLoading((prev) => ({ ...prev, directLinkLogin: true }));
    setDirectLinkState({
      stage: "loading",
      token: clientLinkToken,
      suggestedEmail: "",
      currentLogin: "",
      expiresAt: null,
      fullName: "",
      errorMessage: "",
    });

    authLoginByClientLink(clientLinkToken)
      .then((result) => {
        if (cancelled) return;
        const resolvedEmail = String(
          result?.email ??
          result?.generatedEmail ??
          result?.loginEmail ??
          result?.me?.email ??
          ""
        ).trim();
        const resolvedName = String(
          result?.me?.name ??
          result?.me?.fullName ??
          result?.fullName ??
          ""
        ).trim();
        setDirectLinkState({
          stage: "setup",
          token: clientLinkToken,
          suggestedEmail: resolvedEmail,
          currentLogin: resolvedEmail,
          expiresAt: result?.expiresAt ?? result?.expiredAt ?? result?.validUntil ?? null,
          fullName: resolvedName,
          errorMessage: "",
        });
        setDirectLinkSetup((prev) => ({
          ...prev,
          email: resolvedEmail || prev.email,
          password: "",
          confirmPassword: "",
        }));
      })
      .catch((error) => {
        if (cancelled) return;
        setDirectLinkState({
          stage: "error",
          token: clientLinkToken,
          suggestedEmail: "",
          currentLogin: "",
          expiresAt: null,
          fullName: "",
          errorMessage: mapClientLinkError(error, "Ссылка входа недействительна или уже истекла"),
        });
      })
      .finally(() => {
        if (!cancelled) {
          setLoading((prev) => ({ ...prev, directLinkLogin: false }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authLoginByClientLink, clearNotice, clientLinkToken]);

  React.useEffect(() => {
    if (registerCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setRegisterCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [registerCooldown]);

  const inviteAttribution = React.useMemo(
    () => readInviteAttribution(searchParams),
    [searchParams]
  );

  const switchPage = React.useCallback(
    (next) => {
      setPage(next);
      clearNotice();
      if (next === "reset") {
        setResetDone(false);
        if (forgotSentEmail && !resetData.email) {
          setResetData((prev) => ({ ...prev, email: forgotSentEmail }));
        }
      }
    },
    [clearNotice, forgotSentEmail, resetData.email]
  );

  const passwordScore = scorePassword(registerData.password);
  const resetPasswordScore = scorePassword(resetData.newPassword);
  const directLinkPasswordScore = scorePassword(directLinkSetup.password);

  const finishDirectLinkFlow = React.useCallback(() => {
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  const onDirectLinkSetupSubmit = async (event) => {
    event.preventDefault();
    clearNotice();

    const nextErrors = {
      email: "",
      password: "",
      confirmPassword: "",
    };

    if (!isValidEmail(directLinkSetup.email)) nextErrors.email = "Введите корректный email";

    const passwordError = validatePassword(directLinkSetup.password);
    if (passwordError) nextErrors.password = passwordError;

    if (!directLinkSetup.confirmPassword) nextErrors.confirmPassword = "Подтвердите пароль";
    else if (directLinkSetup.password !== directLinkSetup.confirmPassword) {
      nextErrors.confirmPassword = "Пароли не совпадают";
    }

    setDirectLinkErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setLoading((prev) => ({ ...prev, directLinkSetup: true }));
    try {
      await authApi.completeClientLinkProfile({
        token: directLinkState.token,
        email: directLinkSetup.email.trim(),
        password: directLinkSetup.password,
        confirmPassword: directLinkSetup.confirmPassword,
      });
      await boot();
      finishDirectLinkFlow();
    } catch (error) {
      setNotice({ type: "error", text: mapClientLinkError(error, "Не удалось сохранить почту и пароль") });
    } finally {
      setLoading((prev) => ({ ...prev, directLinkSetup: false }));
    }
  };

  const onDirectLinkSkip = async () => {
    clearNotice();
    setLoading((prev) => ({ ...prev, directLinkSetup: true }));
    try {
      await authApi.completeClientLinkProfile({
        token: directLinkState.token,
        skip: true,
      }).catch((error) => {
        const status = Number(error?.status || 0);
        if ([404, 405, 501].includes(status)) return null;
        throw error;
      });
      finishDirectLinkFlow();
    } catch (error) {
      setNotice({ type: "error", text: mapClientLinkError(error, "Не удалось пропустить настройку") });
    } finally {
      setLoading((prev) => ({ ...prev, directLinkSetup: false }));
    }
  };

  const onLogin = async (event) => {
    event.preventDefault();
    clearNotice();

    const nextErrors = { email: "", password: "", captcha: "" };
    if (!isValidEmail(loginData.email)) nextErrors.email = "Введите корректный email";
    if (!loginData.password) nextErrors.password = "Введите пароль";
    if (captchaEnabled && !captchaTokens.login) nextErrors.captcha = "Подтвердите капчу";

    setLoginErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setLoading((prev) => ({ ...prev, login: true }));
    try {
      await authLogin(loginData.email.trim(), loginData.password, captchaTokens.login || null);
      const target = typeof location.state?.from === "string" ? location.state.from : "/";
      navigate(target || "/", { replace: true });
    } catch (error) {
      setNotice({ type: "error", text: mapApiError(error, "Не удалось войти") });
      setCaptchaTokens((prev) => ({ ...prev, login: "" }));
      setCaptchaResetKeys((prev) => ({ ...prev, login: prev.login + 1 }));
    } finally {
      setLoading((prev) => ({ ...prev, login: false }));
    }
  };

  const onRegister = async (event) => {
    event.preventDefault();
    clearNotice();

    const nextErrors = {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      policy: "",
      age: "",
      captcha: "",
      code: "",
    };

    const fullNameRaw = `${registerData.firstName || ""} ${registerData.lastName || ""}`;
    const cleanName = sanitizeFullNameInput(fullNameRaw).trim().replace(/\s+/g, " ");
    const fullNameError = validateFullName(cleanName);
    if (fullNameError) nextErrors.fullName = fullNameError;

    if (!isValidEmail(registerData.email)) nextErrors.email = "Введите корректный email";

    const passwordError = validatePassword(registerData.password);
    if (passwordError) nextErrors.password = passwordError;

    if (!registerData.confirmPassword) nextErrors.confirmPassword = "Подтвердите пароль";
    else if (registerData.password !== registerData.confirmPassword) {
      nextErrors.confirmPassword = "Пароли не совпадают";
    }

    if (!registerData.acceptPolicy) nextErrors.policy = "Подтвердите согласие с документами";
    if (!registerData.acceptAge) nextErrors.age = "Подтвердите возраст или согласие законного представителя";
    if (captchaEnabled && !captchaTokens.register) nextErrors.captcha = "Подтвердите капчу";

    setRegisterErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setLoading((prev) => ({ ...prev, register: true }));
    try {
      const sourcePage = typeof window === "undefined"
        ? `${location.pathname || "/login"}${location.search || ""}`
        : `${window.location.pathname || location.pathname || "/login"}${window.location.search || location.search || ""}`;
      const registerOptions = {
        client: registerData.accountType !== "psychologist",
        acceptPolicy: registerData.acceptPolicy,
        acceptAge: registerData.acceptAge,
        marketing: registerData.marketing,
        legalDocumentVersion: legalDocumentsVersion,
        sourcePage,
      };
      if (registerOptions.client && inviteAttribution) {
        registerOptions.psychologistId = inviteAttribution.psychologistId;
        registerOptions.source = inviteAttribution.source;
      }
      await authApi.register(
        cleanName,
        registerData.email.trim(),
        registerData.password,
        captchaTokens.register || null,
        registerOptions
      );
      setRegisterPendingEmail(registerData.email.trim());
      setRegisterCode(Array.from({ length: CODE_LEN }, () => ""));
      setRegisterStage("verify");
      setRegisterCooldown(RESEND_DEFAULT_SECONDS);
      setNotice({ type: "success", text: "Код подтверждения отправлен на email" });
      if (registerOptions.client) {
        try {
          window.localStorage.removeItem(INVITE_ATTR_KEY);
        } catch {
          // ignore
        }
      }
      setCaptchaTokens((prev) => ({ ...prev, register: "", registerResend: "" }));
      setCaptchaResetKeys((prev) => ({
        ...prev,
        register: prev.register + 1,
        registerResend: prev.registerResend + 1,
      }));
    } catch (error) {
      setNotice({ type: "error", text: mapApiError(error, "Не удалось зарегистрироваться") });
      setCaptchaTokens((prev) => ({ ...prev, register: "" }));
      setCaptchaResetKeys((prev) => ({ ...prev, register: prev.register + 1 }));
    } finally {
      setLoading((prev) => ({ ...prev, register: false }));
    }
  };

  const onRegisterVerify = async (event) => {
    event.preventDefault();
    clearNotice();

    const codeValue = registerCode.join("").trim();
    if (codeValue.length !== CODE_LEN) {
      setRegisterErrors((prev) => ({ ...prev, code: `Введите код из ${CODE_LEN} цифр` }));
      return;
    }

    if (!registerPendingEmail) {
      setNotice({ type: "error", text: "Не найден email для подтверждения. Повторите регистрацию." });
      setRegisterStage("form");
      return;
    }

    setRegisterErrors((prev) => ({ ...prev, code: "" }));
    setLoading((prev) => ({ ...prev, registerVerify: true }));

    try {
      await authApi.registerVerify(registerPendingEmail, codeValue);
      setNotice({ type: "success", text: "Email подтверждён. Теперь войдите в аккаунт." });
      setLoginData((prev) => ({ ...prev, email: registerPendingEmail, password: "" }));
      setRegisterStage("form");
      setPage("login");
    } catch (error) {
      setRegisterErrors((prev) => ({ ...prev, code: mapApiError(error, "Не удалось подтвердить код") }));
    } finally {
      setLoading((prev) => ({ ...prev, registerVerify: false }));
    }
  };

  const onRegisterResend = async () => {
    clearNotice();
    if (!registerPendingEmail || registerCooldown > 0) return;

    if (captchaEnabled && !captchaTokens.registerResend) {
      setNotice({ type: "warn", text: "Для повторной отправки подтвердите капчу" });
      return;
    }

    setLoading((prev) => ({ ...prev, registerResend: true }));
    try {
      await authApi.registerResend(registerPendingEmail, captchaTokens.registerResend || null);
      setRegisterCooldown(RESEND_DEFAULT_SECONDS);
      setNotice({ type: "info", text: "Код отправлен повторно" });
      setCaptchaTokens((prev) => ({ ...prev, registerResend: "" }));
      setCaptchaResetKeys((prev) => ({ ...prev, registerResend: prev.registerResend + 1 }));
    } catch (error) {
      const retry = Number(error?.retryAfterSeconds || 0);
      if (Number.isFinite(retry) && retry > 0) setRegisterCooldown(Math.ceil(retry));
      setNotice({ type: "error", text: mapApiError(error, "Не удалось отправить код повторно") });
      setCaptchaTokens((prev) => ({ ...prev, registerResend: "" }));
      setCaptchaResetKeys((prev) => ({ ...prev, registerResend: prev.registerResend + 1 }));
    } finally {
      setLoading((prev) => ({ ...prev, registerResend: false }));
    }
  };

  const onForgot = async (event) => {
    event.preventDefault();
    clearNotice();

    const nextErrors = { email: "", captcha: "" };
    if (!isValidEmail(forgotEmail)) nextErrors.email = "Введите корректный email";
    if (captchaEnabled && !captchaTokens.forgot) nextErrors.captcha = "Подтвердите капчу";

    setForgotErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setLoading((prev) => ({ ...prev, forgot: true }));
    try {
      const email = forgotEmail.trim();
      await authApi.recoveryRequest(email, captchaTokens.forgot || null);
      setForgotSentEmail(email);
      setResetData((prev) => ({ ...prev, email }));
      setForgotStage("sent");
      setNotice({ type: "success", text: "Код для восстановления отправлен на email" });
      setCaptchaTokens((prev) => ({ ...prev, forgot: "" }));
      setCaptchaResetKeys((prev) => ({ ...prev, forgot: prev.forgot + 1 }));
    } catch (error) {
      setNotice({ type: "error", text: mapApiError(error, "Не удалось отправить письмо") });
      setCaptchaTokens((prev) => ({ ...prev, forgot: "" }));
      setCaptchaResetKeys((prev) => ({ ...prev, forgot: prev.forgot + 1 }));
    } finally {
      setLoading((prev) => ({ ...prev, forgot: false }));
    }
  };

  const onForgotResend = () => {
    if (!forgotSentEmail) return;
    clearNotice();
    setForgotEmail(forgotSentEmail);
    setForgotStage("form");
    setForgotErrors({ email: "", captcha: "" });
    setCaptchaTokens((prev) => ({ ...prev, forgot: "" }));
    setCaptchaResetKeys((prev) => ({ ...prev, forgot: prev.forgot + 1 }));
  };

  const onReset = async (event) => {
    event.preventDefault();
    clearNotice();

    const nextErrors = { email: "", code: "", newPassword: "", confirmPassword: "" };

    if (!isValidEmail(resetData.email)) nextErrors.email = "Введите корректный email";

    const codeValue = resetData.code.join("").trim();
    if (codeValue.length !== CODE_LEN) nextErrors.code = `Введите код из ${CODE_LEN} цифр`;

    const passwordError = validatePassword(resetData.newPassword);
    if (passwordError) nextErrors.newPassword = passwordError;

    if (!resetData.confirmPassword) nextErrors.confirmPassword = "Подтвердите пароль";
    else if (resetData.newPassword !== resetData.confirmPassword) {
      nextErrors.confirmPassword = "Пароли не совпадают";
    }

    setResetErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setLoading((prev) => ({ ...prev, reset: true }));
    try {
      const email = resetData.email.trim();
      await authApi.recoveryVerify(email, codeValue);
      await authApi.recoveryConfirm(email, codeValue, resetData.newPassword);
      setResetDone(true);
      setNotice({ type: "success", text: "Пароль успешно изменён" });
    } catch (error) {
      const text = mapApiError(error, "Не удалось изменить пароль");
      if (isCodeRelatedError(error)) {
        setResetErrors((prev) => ({ ...prev, code: text }));
      } else {
        setNotice({ type: "error", text });
      }
    } finally {
      setLoading((prev) => ({ ...prev, reset: false }));
    }
  };

  if (clientLinkToken) {
    return (
      <div className="b-auth-v2">
        {notice.text ? (
          <div className={`form-alert form-alert--${notice.type || "info"}`.trim()} role="alert" aria-live="polite">
            {notice.text}
          </div>
        ) : null}

        <div className="auth-page show">
          <div className="auth-split">
            <LeftPanel page="login" />

            <div className="auth-right">
              <div className="auth-form-wrap">
                {directLinkState.stage === "loading" ? (
                  <>
                    <div className="form-header">
                      <div className="form-badge">
                        <div className="form-badge-dot" />
                        <span>Прямая авторизация</span>
                      </div>
                      <div className="form-title">
                        Подключаем<br />
                        <span className="accent-mint">кабинет</span>
                      </div>
                      <div className="form-sub">Проверяем ссылку входа и открываем доступ к аккаунту.</div>
                    </div>
                    <div className="success-state show">
                      <div className="success-icon">…</div>
                      <div className="success-title">Авторизуем вас</div>
                      <div className="success-sub">Это займет несколько секунд.</div>
                    </div>
                  </>
                ) : null}

                {directLinkState.stage === "error" ? (
                  <>
                    <div className="form-header">
                      <div className="form-badge">
                        <div className="form-badge-dot" />
                        <span>Ссылка входа</span>
                      </div>
                      <div className="form-title">
                        Ссылка<br />
                        <span className="accent-lav">не сработала</span>
                      </div>
                      <div className="form-sub">{directLinkState.errorMessage || "Попробуйте запросить новую ссылку у психолога."}</div>
                    </div>
                    <div className="switch-row">
                      <button type="button" className="inline-link" onClick={() => navigate("/login", { replace: true })}>
                        Перейти к обычному входу
                      </button>
                    </div>
                  </>
                ) : null}

                {directLinkState.stage === "setup" ? (
                  <>
                    <div className="form-header">
                      <div className="form-badge">
                        <div className="form-badge-dot" />
                        <span>Вход выполнен</span>
                      </div>
                      <div className="form-title">
                        Завершите<br />
                        <span className="accent-mint">настройку</span>
                      </div>
                      <div className="form-sub">
                        {directLinkState.fullName
                          ? `${directLinkState.fullName}, вы вошли по персональной ссылке.`
                          : "Вы вошли по персональной ссылке."}{" "}
                        Можно указать свою почту и пароль сейчас или пропустить шаг.
                      </div>
                    </div>

                    <form className="auth-form" onSubmit={onDirectLinkSetupSubmit}>
                      <div className="field-group">
                        <div className="field">
                          <label className="field-label" htmlFor="direct-link-email">
                            Ваша почта
                          </label>
                          <div className="field-input-wrap">
                            <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path
                                d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                              <polyline
                                points="22,6 12,13 2,6"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <input
                              id="direct-link-email"
                              className={`field-input ${directLinkErrors.email ? "error" : ""}`.trim()}
                              type="email"
                              autoComplete="email"
                              placeholder="you@example.com"
                              value={directLinkSetup.email}
                              onChange={(event) => {
                                setDirectLinkSetup((prev) => ({ ...prev, email: event.target.value }));
                                setDirectLinkErrors((prev) => ({ ...prev, email: "" }));
                              }}
                            />
                          </div>
                          {directLinkErrors.email ? <div className="field-error show">{directLinkErrors.email}</div> : null}
                          {directLinkState.currentLogin ? (
                            <div className="field-hint">Текущий технический логин: {directLinkState.currentLogin}</div>
                          ) : null}
                        </div>

                        <div className="field">
                          <label className="field-label" htmlFor="direct-link-password">
                            Новый пароль
                          </label>
                          <div className="field-input-wrap">
                            <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            <input
                              id="direct-link-password"
                              className={`field-input ${directLinkErrors.password ? "error" : ""}`.trim()}
                              type={directLinkShowPassword ? "text" : "password"}
                              autoComplete="new-password"
                              placeholder="Минимум 8 символов"
                              value={directLinkSetup.password}
                              onChange={(event) => {
                                setDirectLinkSetup((prev) => ({ ...prev, password: event.target.value }));
                                setDirectLinkErrors((prev) => ({ ...prev, password: "" }));
                              }}
                            />
                            <button type="button" className="field-eye" onClick={() => setDirectLinkShowPassword((prev) => !prev)}>
                              {directLinkShowPassword ? "🙈" : "👁"}
                            </button>
                          </div>
                          {directLinkErrors.password ? <div className="field-error show">{directLinkErrors.password}</div> : null}

                          <div className={`pw-strength ${directLinkSetup.password ? "show" : ""}`.trim()}>
                            {Array.from({ length: 4 }).map((_, idx) => {
                              const cls = directLinkPasswordScore <= 1 ? "weak" : directLinkPasswordScore === 2 ? "medium" : "strong";
                              return <div key={`direct-pw-bar-${idx}`} className={`pw-bar ${idx < directLinkPasswordScore ? cls : ""}`.trim()} />;
                            })}
                          </div>
                        </div>

                        <div className="field">
                          <label className="field-label" htmlFor="direct-link-password2">
                            Подтвердите пароль
                          </label>
                          <div className="field-input-wrap">
                            <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            <input
                              id="direct-link-password2"
                              className={`field-input ${directLinkErrors.confirmPassword ? "error" : ""}`.trim()}
                              type={directLinkShowPassword2 ? "text" : "password"}
                              autoComplete="new-password"
                              placeholder="Повторите пароль"
                              value={directLinkSetup.confirmPassword}
                              onChange={(event) => {
                                setDirectLinkSetup((prev) => ({ ...prev, confirmPassword: event.target.value }));
                                setDirectLinkErrors((prev) => ({ ...prev, confirmPassword: "" }));
                              }}
                            />
                            <button type="button" className="field-eye" onClick={() => setDirectLinkShowPassword2((prev) => !prev)}>
                              {directLinkShowPassword2 ? "🙈" : "👁"}
                            </button>
                          </div>
                          {directLinkErrors.confirmPassword ? <div className="field-error show">{directLinkErrors.confirmPassword}</div> : null}
                          {directLinkState.expiresAt ? (
                            <div className="field-hint">Ссылка действует до {formatDateTimeShort(directLinkState.expiresAt)}</div>
                          ) : null}
                        </div>
                      </div>

                      <button
                        type="submit"
                        className={`btn-submit ${loading.directLinkSetup ? "loading" : ""}`.trim()}
                        disabled={loading.directLinkSetup}
                      >
                        <span className="spinner" />
                        <span className="btn-text">Сохранить и продолжить →</span>
                      </button>
                    </form>

                    <div className="switch-row">
                      <button type="button" className="inline-link" onClick={onDirectLinkSkip} disabled={loading.directLinkSetup}>
                        Пропустить сейчас
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="b-auth-v2">
      {notice.text ? (
        <div className={`form-alert form-alert--${notice.type || "info"}`.trim()} role="alert" aria-live="polite">
          {notice.text}
        </div>
      ) : null}

      <div className={`auth-page ${page === "login" ? "show" : ""}`.trim()}>
        <div className="auth-split">
          <LeftPanel page="login" />

          <div className="auth-right">
            <div className="auth-form-wrap">
              <div className="form-header">
                <div className="form-badge">
                  <div className="form-badge-dot" />
                  <span>Безопасный вход</span>
                </div>
                <div className="form-title">
                  С возвращением<span className="accent-mint"> 👋</span>
                </div>
                <div className="form-sub">
                  Нет аккаунта?{" "}
                  <button type="button" className="inline-link" onClick={() => switchPage("register")}>
                    Зарегистрироваться
                  </button>
                </div>
              </div>

              <form className="auth-form" onSubmit={onLogin}>
                <div className="field-group">
                  <div className="field">
                    <label className="field-label" htmlFor="login-email">
                      Email
                    </label>
                    <div className="field-input-wrap">
                      <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <polyline
                          points="22,6 12,13 2,6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <input
                        id="login-email"
                        className={`field-input ${loginErrors.email ? "error" : ""}`.trim()}
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={loginData.email}
                        onChange={(event) => setLoginData((prev) => ({ ...prev, email: event.target.value }))}
                      />
                    </div>
                    {loginErrors.email ? <div className="field-error show">{loginErrors.email}</div> : null}
                  </div>

                  <div className="field">
                    <div className="row-between">
                      <label className="field-label" htmlFor="login-password">
                        Пароль
                      </label>
                      <button type="button" className="forgot-link" onClick={() => switchPage("forgot")}>
                        Забыли пароль?
                      </button>
                    </div>
                    <div className="field-input-wrap">
                      <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
                        <path
                          d="M7 11V7a5 5 0 0 1 10 0v4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      <input
                        id="login-password"
                        className={`field-input ${loginErrors.password ? "error" : ""}`.trim()}
                        type={loginShowPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(event) => setLoginData((prev) => ({ ...prev, password: event.target.value }))}
                      />
                      <button type="button" className="field-eye" onClick={() => setLoginShowPassword((prev) => !prev)}>
                        {loginShowPassword ? "🙈" : "👁"}
                      </button>
                    </div>
                    {loginErrors.password ? <div className="field-error show">{loginErrors.password}</div> : null}
                  </div>
                </div>

                <div className="field-group compact-gap">
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={loginData.remember}
                      onChange={(event) => setLoginData((prev) => ({ ...prev, remember: event.target.checked }))}
                    />
                    <div className={`check-box ${loginData.remember ? "checked" : ""}`.trim()} />
                    <span className="check-label">Запомнить меня на этом устройстве</span>
                  </label>
                </div>

                {captchaEnabled ? (
                  <>
                    <SmartCaptchaBox
                      siteKey={CAPTCHA_SITE_KEY}
                      resetKey={captchaResetKeys.login}
                      disabled={loading.login}
                      onToken={(token) => {
                        setCaptchaTokens((prev) => ({ ...prev, login: token }));
                        setLoginErrors((prev) => ({ ...prev, captcha: "" }));
                      }}
                      onError={(error) => {
                        if (isSmartCaptchaSuspendedError(error)) {
                          onCaptchaSuspended();
                          return;
                        }
                        setLoginErrors((prev) => ({ ...prev, captcha: "Ошибка загрузки капчи" }));
                      }}
                    />
                    {loginErrors.captcha ? <div className="field-error show">{loginErrors.captcha}</div> : null}
                  </>
                ) : captchaHintText ? (
                  <div className="field-hint">{captchaHintText}</div>
                ) : null}

                <button type="submit" className={`btn-submit ${loading.login ? "loading" : ""}`.trim()} disabled={loading.login}>
                  <span className="spinner" />
                  <span className="btn-text">Войти →</span>
                </button>

                <SocialAuthRow yandexAuthUrl={YANDEX_AUTH_URL} />
              </form>

              <div className="switch-row">
                Впервые здесь?{" "}
                <button type="button" className="inline-link" onClick={() => switchPage("register")}>
                  Создать аккаунт
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`auth-page ${page === "register" ? "show" : ""}`.trim()}>
        <div className="auth-split">
          <LeftPanel page="register" />

          <div className="auth-right">
            <div className="auth-form-wrap">
              {registerStage === "form" ? (
                <>
                  <div className="form-header">
                    <div className="form-badge">
                      <div className="form-badge-dot" />
                      <span>7 дней бесплатно</span>
                    </div>
                    <div className="form-title">
                      Создать<br />
                      <span className="accent-lav">аккаунт</span>
                    </div>
                    <div className="form-sub">
                      Уже есть аккаунт?{" "}
                      <button type="button" className="inline-link" onClick={() => switchPage("login")}>
                        Войти
                      </button>
                    </div>
                  </div>

                  <form className="auth-form" onSubmit={onRegister}>
                    <div className="field-group">
                      <div className="field-grid-2">
                        <div className="field">
                          <label className="field-label" htmlFor="register-firstname">
                            Имя
                          </label>
                          <div className="field-input-wrap">
                            <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path
                                d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                            </svg>
                            <input
                              id="register-firstname"
                              className={`field-input ${registerErrors.fullName ? "error" : ""}`.trim()}
                              type="text"
                              placeholder="Анна"
                              autoComplete="given-name"
                              value={registerData.firstName}
                              onChange={(event) =>
                                setRegisterData((prev) => ({
                                  ...prev,
                                  firstName: sanitizeFullNameInput(event.target.value),
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="field">
                          <label className="field-label" htmlFor="register-lastname">
                            Фамилия
                          </label>
                          <div className="field-input-wrap">
                            <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path
                                d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                            </svg>
                            <input
                              id="register-lastname"
                              className={`field-input ${registerErrors.fullName ? "error" : ""}`.trim()}
                              type="text"
                              placeholder="Кузнецова"
                              autoComplete="family-name"
                              value={registerData.lastName}
                              onChange={(event) =>
                                setRegisterData((prev) => ({
                                  ...prev,
                                  lastName: sanitizeFullNameInput(event.target.value),
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                      {registerErrors.fullName ? <div className="field-error show">{registerErrors.fullName}</div> : null}

                      <div className="field">
                        <label className="field-label" htmlFor="register-email">
                          Email
                        </label>
                        <div className="field-input-wrap">
                          <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <polyline
                              points="22,6 12,13 2,6"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            id="register-email"
                            className={`field-input ${registerErrors.email ? "error" : ""}`.trim()}
                            type="email"
                            placeholder="you@example.com"
                            autoComplete="email"
                            value={registerData.email}
                            onChange={(event) => setRegisterData((prev) => ({ ...prev, email: event.target.value }))}
                          />
                        </div>
                        {registerErrors.email ? <div className="field-error show">{registerErrors.email}</div> : null}
                      </div>

                      <div className="field">
                        <label className="field-label">Роль при регистрации</label>
                        <div className="auth-role-toggle" role="group" aria-label="Роль пользователя">
                          <button
                            type="button"
                            className={`auth-role-toggle__btn ${registerData.accountType === "client" ? "is-active" : ""}`.trim()}
                            onClick={() => setRegisterData((prev) => ({ ...prev, accountType: "client" }))}
                          >
                            Клиент
                          </button>
                          <button
                            type="button"
                            className={`auth-role-toggle__btn ${registerData.accountType === "psychologist" ? "is-active" : ""}`.trim()}
                            onClick={() => setRegisterData((prev) => ({ ...prev, accountType: "psychologist" }))}
                          >
                            Психолог
                          </button>
                        </div>
                        <div className="field-hint">
                          {registerData.accountType === "psychologist"
                            ? "Для психологов создание материалов откроется после верификации администратором."
                            : "Клиентский аккаунт создается сразу после подтверждения email."}
                        </div>
                      </div>

                      <div className="field">
                        <label className="field-label" htmlFor="register-password">
                          Пароль
                        </label>
                        <div className="field-input-wrap">
                          <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
                            <path
                              d="M7 11V7a5 5 0 0 1 10 0v4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          <input
                            id="register-password"
                            className={`field-input ${registerErrors.password ? "error" : ""}`.trim()}
                            type={registerShowPassword ? "text" : "password"}
                            placeholder="Минимум 8 символов"
                            autoComplete="new-password"
                            value={registerData.password}
                            onChange={(event) => setRegisterData((prev) => ({ ...prev, password: event.target.value }))}
                          />
                          <button type="button" className="field-eye" onClick={() => setRegisterShowPassword((prev) => !prev)}>
                            {registerShowPassword ? "🙈" : "👁"}
                          </button>
                        </div>
                        {registerErrors.password ? <div className="field-error show">{registerErrors.password}</div> : null}

                        <div className={`pw-strength ${registerData.password ? "show" : ""}`.trim()}>
                          {Array.from({ length: 4 }).map((_, idx) => {
                            const cls = passwordScore <= 1 ? "weak" : passwordScore === 2 ? "medium" : "strong";
                            return <div key={`reg-pw-bar-${idx}`} className={`pw-bar ${idx < passwordScore ? cls : ""}`.trim()} />;
                          })}
                        </div>
                        <div
                          className={`pw-label ${registerData.password ? "show" : ""} ${
                            passwordScore <= 1 ? "weak" : passwordScore === 2 ? "medium" : "strong"
                          }`.trim()}
                        >
                          {passwordScore <= 1
                            ? "Слабый пароль"
                            : passwordScore === 2
                            ? "Средний пароль"
                            : passwordScore === 3
                            ? "Надёжный пароль"
                            : "Отличный пароль ✓"}
                        </div>
                      </div>

                      <div className="field">
                        <label className="field-label" htmlFor="register-password2">
                          Подтвердите пароль
                        </label>
                        <div className="field-input-wrap">
                          <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
                            <path
                              d="M7 11V7a5 5 0 0 1 10 0v4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          <input
                            id="register-password2"
                            className={`field-input ${registerErrors.confirmPassword ? "error" : ""}`.trim()}
                            type={registerShowPassword2 ? "text" : "password"}
                            placeholder="Повторите пароль"
                            autoComplete="new-password"
                            value={registerData.confirmPassword}
                            onChange={(event) => setRegisterData((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                          />
                          <button type="button" className="field-eye" onClick={() => setRegisterShowPassword2((prev) => !prev)}>
                            {registerShowPassword2 ? "🙈" : "👁"}
                          </button>
                        </div>
                        {registerErrors.confirmPassword ? <div className="field-error show">{registerErrors.confirmPassword}</div> : null}
                      </div>
                    </div>

                    <div className="field-group compact-gap">
                      <label className="check-row">
                        <input
                          type="checkbox"
                          checked={registerData.acceptPolicy}
                          onChange={(event) => setRegisterData((prev) => ({ ...prev, acceptPolicy: event.target.checked }))}
                        />
                        <div className={`check-box ${registerData.acceptPolicy ? "checked" : ""}`.trim()} />
                        <span className="check-label">
                          <span className="check-kicker">Обязательное согласие</span>
                          Я принимаю <Link to={publicOffer}>Публичную оферту</Link>,{" "}
                          <Link to={userAgreement}>Пользовательское соглашение</Link>,{" "}
                          <Link to={privacyPolicy}>Политику конфиденциальности</Link>,{" "}
                          <Link to={refundPolicy}>Политику отмены, переноса и возврата</Link> и даю согласие на{" "}
                          <Link to={personalData}>обработку персональных данных</Link>
                        </span>
                      </label>
                      {registerErrors.policy ? <div className="field-error show">{registerErrors.policy}</div> : null}

                      <label className="check-row">
                        <input
                          type="checkbox"
                          checked={registerData.acceptAge}
                          onChange={(event) => setRegisterData((prev) => ({ ...prev, acceptAge: event.target.checked }))}
                        />
                        <div className={`check-box ${registerData.acceptAge ? "checked" : ""}`.trim()} />
                        <span className="check-label"><span className="check-kicker">Обязательное подтверждение</span>Подтверждаю, что мне исполнилось 18 лет</span>
                      </label>
                      {registerErrors.age ? <div className="field-error show">{registerErrors.age}</div> : null}

                      <label className="check-row">
                        <input
                          type="checkbox"
                          checked={registerData.marketing}
                          onChange={(event) => setRegisterData((prev) => ({ ...prev, marketing: event.target.checked }))}
                        />
                        <div className={`check-box ${registerData.marketing ? "checked" : ""}`.trim()} />
                        <span className="check-label"><span className="check-kicker">Рассылка — необязательно</span>Хочу получать полезные материалы, новости и обновления Платформы</span>
                      </label>
                    </div>

                    {captchaEnabled ? (
                      <>
                        <SmartCaptchaBox
                          siteKey={CAPTCHA_SITE_KEY}
                          resetKey={captchaResetKeys.register}
                          disabled={loading.register}
                          onToken={(token) => {
                            setCaptchaTokens((prev) => ({ ...prev, register: token }));
                            setRegisterErrors((prev) => ({ ...prev, captcha: "" }));
                          }}
                          onError={(error) => {
                            if (isSmartCaptchaSuspendedError(error)) {
                              onCaptchaSuspended();
                              return;
                            }
                            setRegisterErrors((prev) => ({ ...prev, captcha: "Ошибка загрузки капчи" }));
                          }}
                        />
                        {registerErrors.captcha ? <div className="field-error show">{registerErrors.captcha}</div> : null}
                      </>
                    ) : captchaHintText ? (
                      <div className="field-hint">{captchaHintText}</div>
                    ) : null}

                    <button
                      type="submit"
                      className={`btn-submit ${loading.register ? "loading" : ""}`.trim()}
                      disabled={loading.register}
                    >
                      <span className="spinner" />
                      <span className="btn-text">Создать аккаунт →</span>
                    </button>

                    <SocialAuthRow yandexAuthUrl={YANDEX_AUTH_URL} />
                  </form>

                  <div className="switch-row">
                    Уже зарегистрированы?{" "}
                    <button type="button" className="inline-link" onClick={() => switchPage("login")}>
                      Войти
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button type="button" className="back-link-auth" onClick={() => setRegisterStage("form")}>
                    ← Назад к регистрации
                  </button>
                  <div className="form-header">
                    <div className="form-title">
                      Подтвердите<br />
                      <span className="accent-mint">email</span>
                    </div>
                    <div className="form-sub">Мы отправили 5-значный код на {registerPendingEmail || registerData.email}</div>
                  </div>

                  <form className="auth-form" onSubmit={onRegisterVerify}>
                    <OtpInputGroup
                      idPrefix="register-code"
                      value={registerCode}
                      onChange={(digits) => {
                        setRegisterCode(digits);
                        setRegisterErrors((prev) => ({ ...prev, code: "" }));
                      }}
                      disabled={loading.registerVerify}
                    />
                    {registerErrors.code ? <div className="field-error show center">{registerErrors.code}</div> : null}

                    <button
                      type="submit"
                      className={`btn-submit ${loading.registerVerify ? "loading" : ""}`.trim()}
                      disabled={loading.registerVerify}
                    >
                      <span className="spinner" />
                      <span className="btn-text">Подтвердить код →</span>
                    </button>
                  </form>

                  <div className="resend-row">
                    {registerCooldown > 0 ? (
                      <>Повторная отправка через {registerCooldown} сек.</>
                    ) : (
                      <button
                        type="button"
                        className="resend-link-btn"
                        onClick={onRegisterResend}
                        disabled={loading.registerResend}
                      >
                        {loading.registerResend ? "Отправляем..." : "Отправить код повторно"}
                      </button>
                    )}
                  </div>

                  {captchaEnabled ? (
                    <SmartCaptchaBox
                      siteKey={CAPTCHA_SITE_KEY}
                      resetKey={captchaResetKeys.registerResend}
                      disabled={loading.registerResend || registerCooldown > 0}
                      onToken={(token) => setCaptchaTokens((prev) => ({ ...prev, registerResend: token }))}
                      onError={(error) => {
                        if (isSmartCaptchaSuspendedError(error)) {
                          onCaptchaSuspended();
                          return;
                        }
                        setNotice({ type: "error", text: "Ошибка загрузки капчи для повторной отправки" });
                      }}
                    />
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`auth-page ${page === "forgot" ? "show" : ""}`.trim()}>
        <div className="auth-split">
          <LeftPanel page="forgot" />

          <div className="auth-right">
            <div className="auth-form-wrap">
              <button type="button" className="back-link-auth" onClick={() => switchPage("login")}>
                ← Вернуться ко входу
              </button>

              {forgotStage === "form" ? (
                <>
                  <div className="form-header">
                    <div className="form-title">
                      Восстановить<br />
                      <span className="accent-mint">пароль</span>
                    </div>
                    <div className="form-sub">Введите email аккаунта — отправим код подтверждения.</div>
                  </div>

                  <form className="auth-form" onSubmit={onForgot}>
                    <div className="field-group">
                      <div className="field">
                        <label className="field-label" htmlFor="forgot-email">
                          Email
                        </label>
                        <div className="field-input-wrap">
                          <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <polyline
                              points="22,6 12,13 2,6"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            id="forgot-email"
                            className={`field-input ${forgotErrors.email ? "error" : ""}`.trim()}
                            type="email"
                            placeholder="you@example.com"
                            autoComplete="email"
                            value={forgotEmail}
                            onChange={(event) => setForgotEmail(event.target.value)}
                          />
                        </div>
                        {forgotErrors.email ? <div className="field-error show">{forgotErrors.email}</div> : null}
                        <div className="field-hint">Код действует 10 минут.</div>
                      </div>
                    </div>

                    {captchaEnabled ? (
                      <>
                        <SmartCaptchaBox
                          siteKey={CAPTCHA_SITE_KEY}
                          resetKey={captchaResetKeys.forgot}
                          disabled={loading.forgot}
                          onToken={(token) => {
                            setCaptchaTokens((prev) => ({ ...prev, forgot: token }));
                            setForgotErrors((prev) => ({ ...prev, captcha: "" }));
                          }}
                          onError={(error) => {
                            if (isSmartCaptchaSuspendedError(error)) {
                              onCaptchaSuspended();
                              return;
                            }
                            setForgotErrors((prev) => ({ ...prev, captcha: "Ошибка загрузки капчи" }));
                          }}
                        />
                        {forgotErrors.captcha ? <div className="field-error show">{forgotErrors.captcha}</div> : null}
                      </>
                    ) : captchaHintText ? (
                      <div className="field-hint">{captchaHintText}</div>
                    ) : null}

                    <button type="submit" className={`btn-submit ${loading.forgot ? "loading" : ""}`.trim()} disabled={loading.forgot}>
                      <span className="spinner" />
                      <span className="btn-text">Отправить код →</span>
                    </button>
                  </form>

                  <div className="switch-row">
                    Вспомнили пароль?{" "}
                    <button type="button" className="inline-link" onClick={() => switchPage("login")}>
                      Войти
                    </button>
                  </div>
                </>
              ) : (
                <div className="success-state show">
                  <div className="success-icon">✓</div>
                  <div className="success-title">Код отправлен</div>
                  <div className="success-sub">
                    Мы отправили код восстановления на <strong>{forgotSentEmail}</strong>.
                  </div>
                  <button type="button" className="btn-submit" onClick={() => switchPage("reset")}>
                    Ввести код и новый пароль →
                  </button>
                  <div className="resend-row">
                    Не получили?{" "}
                    <button type="button" className="resend-link-btn" onClick={onForgotResend}>
                      Отправить ещё раз
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`auth-page ${page === "reset" ? "show" : ""}`.trim()}>
        <div className="auth-split">
          <LeftPanel page="reset" />

          <div className="auth-right">
            <div className="auth-form-wrap">
              <div className="step-row">
                <div className="step-item done">
                  <div className="step-circle">✓</div>
                  <span className="step-label">Запрос</span>
                </div>
                <div className="step-item done">
                  <div className="step-circle">✓</div>
                  <span className="step-label">Подтверждение</span>
                </div>
                <div className={`step-item ${resetDone ? "done" : "active"}`.trim()}>
                  <div className="step-circle">{resetDone ? "✓" : "3"}</div>
                  <span className="step-label">Новый пароль</span>
                </div>
              </div>

              {!resetDone ? (
                <>
                  <div className="form-header">
                    <div className="form-title">
                      Новый<br />
                      <span className="accent-lav">пароль</span>
                    </div>
                    <div className="form-sub">Введите email, код из письма и новый пароль.</div>
                  </div>

                  <form className="auth-form" onSubmit={onReset}>
                    <div className="field-group">
                      <div className="field">
                        <label className="field-label" htmlFor="reset-email">
                          Email
                        </label>
                        <div className="field-input-wrap">
                          <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <polyline
                              points="22,6 12,13 2,6"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            id="reset-email"
                            className={`field-input ${resetErrors.email ? "error" : ""}`.trim()}
                            type="email"
                            placeholder="you@example.com"
                            autoComplete="email"
                            value={resetData.email}
                            onChange={(event) => setResetData((prev) => ({ ...prev, email: event.target.value }))}
                          />
                        </div>
                        {resetErrors.email ? <div className="field-error show">{resetErrors.email}</div> : null}
                      </div>

                      <div className="field">
                        <label className="field-label">Код из письма</label>
                        <OtpInputGroup
                          idPrefix="reset-code"
                          value={resetData.code}
                          onChange={(digits) => {
                            setResetData((prev) => ({ ...prev, code: digits }));
                            setResetErrors((prev) => ({ ...prev, code: "" }));
                          }}
                          disabled={loading.reset}
                        />
                        {resetErrors.code ? <div className="field-error show center">{resetErrors.code}</div> : null}
                      </div>

                      <div className="field">
                        <label className="field-label" htmlFor="reset-password">
                          Новый пароль
                        </label>
                        <div className="field-input-wrap">
                          <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
                            <path
                              d="M7 11V7a5 5 0 0 1 10 0v4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          <input
                            id="reset-password"
                            className={`field-input ${resetErrors.newPassword ? "error" : ""}`.trim()}
                            type={resetShowPassword ? "text" : "password"}
                            placeholder="Минимум 8 символов"
                            autoComplete="new-password"
                            value={resetData.newPassword}
                            onChange={(event) => setResetData((prev) => ({ ...prev, newPassword: event.target.value }))}
                          />
                          <button type="button" className="field-eye" onClick={() => setResetShowPassword((prev) => !prev)}>
                            {resetShowPassword ? "🙈" : "👁"}
                          </button>
                        </div>
                        {resetErrors.newPassword ? <div className="field-error show">{resetErrors.newPassword}</div> : null}

                        <div className={`pw-strength ${resetData.newPassword ? "show" : ""}`.trim()}>
                          {Array.from({ length: 4 }).map((_, idx) => {
                            const cls = resetPasswordScore <= 1 ? "weak" : resetPasswordScore === 2 ? "medium" : "strong";
                            return <div key={`reset-pw-bar-${idx}`} className={`pw-bar ${idx < resetPasswordScore ? cls : ""}`.trim()} />;
                          })}
                        </div>
                      </div>

                      <div className="field">
                        <label className="field-label" htmlFor="reset-password2">
                          Подтвердите новый пароль
                        </label>
                        <div className="field-input-wrap">
                          <svg className="fi-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
                            <path
                              d="M7 11V7a5 5 0 0 1 10 0v4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          <input
                            id="reset-password2"
                            className={`field-input ${resetErrors.confirmPassword ? "error" : ""}`.trim()}
                            type={resetShowPassword2 ? "text" : "password"}
                            placeholder="Повторите пароль"
                            autoComplete="new-password"
                            value={resetData.confirmPassword}
                            onChange={(event) => setResetData((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                          />
                          <button type="button" className="field-eye" onClick={() => setResetShowPassword2((prev) => !prev)}>
                            {resetShowPassword2 ? "🙈" : "👁"}
                          </button>
                        </div>
                        {resetErrors.confirmPassword ? <div className="field-error show">{resetErrors.confirmPassword}</div> : null}
                      </div>
                    </div>

                    <button type="submit" className={`btn-submit ${loading.reset ? "loading" : ""}`.trim()} disabled={loading.reset}>
                      <span className="spinner" />
                      <span className="btn-text">Сохранить пароль →</span>
                    </button>
                  </form>
                </>
              ) : (
                <div className="success-state show">
                  <div className="success-icon">✓</div>
                  <div className="success-title">Пароль обновлён!</div>
                  <div className="success-sub">Теперь вы можете войти с новым паролем.</div>
                  <button type="button" className="btn-submit" onClick={() => switchPage("login")}>
                    Войти →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
